import type {
  ResourcePointGeoStatus,
  ResourcePointListFilters,
  ResourcePointListParams,
  ResourcePointListScope,
  ResourcePointMediaScene,
  ResourcePointSettlementUnit,
  ResourcePointSortField,
  ResourcePointStatus,
  ResourcePointSubType,
  ResourcePointTicketResourceType,
  ResourcePointTravelerRole,
} from "../types";

/** 资源点列表菜单范围类型。 */
export type { ResourcePointListScope } from "../types";

/** 考察点二级类型值集合。 */
export const resourcePointSubTypes = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "21",
  "22",
] as const satisfies readonly ResourcePointSubType[];

/** SKU 票种资源类型值集合。 */
export const resourcePointTicketResourceTypes = [
  "entrance_ticket",
  "sub_attraction",
  "cableway",
  "scenic_transport",
  "boat",
  "mini_train",
  "guide",
  "audio_guide",
  "experience",
  "performance",
  "other",
] as const satisfies readonly ResourcePointTicketResourceType[];

/** 出行人角色值集合。 */
export const resourcePointTravelerRoles = [
  "student",
  "teacher",
  "guide",
  "driver",
] as const satisfies readonly ResourcePointTravelerRole[];

/** SKU 结算单位值集合。 */
export const resourcePointSettlementUnits = [
  "人",
  "团",
] as const satisfies readonly ResourcePointSettlementUnit[];

/** 资源点状态值集合。 */
export const resourcePointStatuses = [
  "active",
  "inactive",
] as const satisfies readonly ResourcePointStatus[];

/** 资源点列表排序字段集合。 */
export const resourcePointSortFields = [
  "point_name",
  "point_type",
  "province",
  "city",
  "status",
  "is_disabled",
  "itinerary_count",
  "order_itinerary_count",
  "created_at",
  "updated_at",
] as const satisfies readonly ResourcePointSortField[];

/** 资源点列表默认查询参数。 */
export const resourcePointDefaultListParams = {
  page: 1,
  pageSize: 20,
  sortField: "updated_at",
  sortDirection: "desc",
} as const satisfies Pick<
  ResourcePointListParams,
  "page" | "pageSize" | "sortField" | "sortDirection"
>;

/** 考察点二级类型选项。 */
export const resourcePointSubTypeOptions = [
  { value: "1", label: "景点" },
  { value: "2", label: "企业" },
  { value: "3", label: "博物馆/美术馆/艺术展" },
  { value: "4", label: "学校" },
  { value: "5", label: "创意园区" },
  { value: "6", label: "非遗/手工艺" },
  { value: "7", label: "古村落/古街" },
  { value: "8", label: "商业街/商场/集市" },
  { value: "21", label: "特色博物馆" },
  { value: "22", label: "高校考察" },
] as const satisfies ReadonlyArray<{ value: ResourcePointSubType; label: string }>;

/** SKU 票种资源类型选项。 */
export const resourcePointTicketResourceTypeOptions = [
  { value: "entrance_ticket", label: "门票" },
  { value: "sub_attraction", label: "小景点" },
  { value: "cableway", label: "索道" },
  { value: "scenic_transport", label: "景交车" },
  { value: "boat", label: "游船" },
  { value: "mini_train", label: "小火车" },
  { value: "guide", label: "讲解服务" },
  { value: "audio_guide", label: "耳麦（讲解）" },
  { value: "experience", label: "体验课程" },
  { value: "performance", label: "表演" },
  { value: "other", label: "其他" },
] as const satisfies ReadonlyArray<{
  value: ResourcePointTicketResourceType;
  label: string;
}>;

/** 出行人角色选项。 */
export const resourcePointTravelerRoleOptions = [
  { value: "student", label: "学生" },
  { value: "teacher", label: "老师" },
  { value: "guide", label: "导游" },
  { value: "driver", label: "司机" },
] as const satisfies ReadonlyArray<{ value: ResourcePointTravelerRole; label: string }>;

/** 考察点一级类型选项。 */
export const resourcePointTypeOptions = [
  { value: "考察点", label: "考察点" },
  { value: "火车站", label: "火车站" },
  { value: "车站", label: "车站" },
  { value: "机场", label: "机场" },
] as const;

/** 需要二级类型的考察点一级类型。 */
export const resourcePointTypeWithSubType = "考察点";


/** 返回菜单范围锁定的列表筛选。 */
export function scopeLockedListFilters(
  scope: ResourcePointListScope,
): Pick<ResourcePointListFilters, "pointType" | "pointSubType"> {
  if (scope === "enterprise") {
    return { pointType: "考察点", pointSubType: "2" };
  }
  if (scope === "intangible") {
    return { pointType: "考察点", pointSubType: "6" };
  }
  if (scope === "other") {
    return { pointType: "考察点", pointSubType: "1,3,4,5,7,8,21,22" };
  }
  if (scope === "railway") {
    return { pointType: "火车站,车站" };
  }
  if (scope === "airport") {
    return { pointType: "机场" };
  }
  return {};
}

/** 菜单范围是否隐藏一级/二级类型筛选。 */
export function scopeHidesTypeFilters(scope: ResourcePointListScope): boolean {
  return scope !== "all";
}

/** 返回与侧栏菜单一致的实体名称，用于新增按钮与抽屉文案。 */
export function scopeEntityLabel(scope: ResourcePointListScope): string {
  if (scope === "enterprise") return "企业";
  if (scope === "intangible") return "非遗";
  if (scope === "railway") return "火车站";
  if (scope === "airport") return "机场";
  return "考察点";
}

/** 将菜单锁定的类型写入 SPU 变更载荷（新增默认与提交兜底共用）。 */
export function applyScopeLockedSpuDefaults<T extends {
  pointType: string;
  pointSubType?: string;
}>(scope: ResourcePointListScope, payload: T): T {
  const locked = scopeLockedListFilters(scope);
  if (!locked.pointType && locked.pointSubType === undefined) {
    return payload;
  }
  const mutationPointType = scope === "railway" ? "火车站" : locked.pointType;
  const mutationPointSubType = scope === "other" ? payload.pointSubType : locked.pointSubType;
  return {
    ...payload,
    ...(mutationPointType ? { pointType: mutationPointType } : {}),
    pointSubType: mutationPointSubType,
  };
}

/** GEO 识别状态筛选选项。 */
export const resourcePointGeoStatusFilterOptions = [
  { value: "unverified", label: "未识别" },
  { value: "confirmed", label: "已识别" },
] as const satisfies ReadonlyArray<{ value: ResourcePointGeoStatus; label: string }>;

/** 资源点状态筛选选项。 */
export const resourcePointStatusOptions = [
  { value: "active", label: "有效" },
  { value: "inactive", label: "无效" },
] as const satisfies ReadonlyArray<{ value: ResourcePointStatus; label: string }>;

/** 资源点素材场景选项。 */
export const resourcePointMediaSceneOptions = [
  { value: "teaching", label: "教学" },
  { value: "product", label: "产品" },
  { value: "introduction", label: "传承人" },
  { value: "ppt", label: "PPT" },
  { value: "description", label: "描述" },
] as const satisfies ReadonlyArray<{ value: ResourcePointMediaScene; label: string }>;

/** 非遗描述场景标签。 */
export const resourcePointHeritageDescriptionLabels = {
  brief: "简介",
  visitFlow: "参观流程",
  experienceFlow: "体验流程",
  priceOther: "价格及其他",
} as const;
